<template>
  <div ref="resizableElm" class="helpCamSelect">
    <help-header />
    <header></header>
    <v-divider class="helpCamSelect_divider" />
    <section class="helpCamSelect_wrapper">
      <main>
        <section
          class="helpCamSelect_left"
          :style="{
            'max-height': `${resizableHeight - 105}px`,
          }"
        >
          <custom-check
            v-for="(item, index) in items"
            :key="index"
            v-model="item.value"
            v-ripple
            only-check
            check-color="#0075c2"
            :class="{
              ['helpCamSelect_item--selected']:
                selectedCam && selectedCam.id === item.id,
            }"
            no-circle
            @input="handleCheck($event, index)"
            >{{ item.label }}
            <template #append>
              <span class="helpCamSelect_flex-gap"></span>
              <img src="@/assets/icons/ico_camera.svg" />
            </template>
          </custom-check>
        </section>
        <section class="helpCamSelect_right">
          <section class="helpCamSelect_cam">
            <video-stream
              v-if="selectedCamWithCredentials && showVideo"
              :id="selectedCamWithCredentials.id"
              :stream-config="{
                accessKeyId: selectedCamWithCredentials.accessKeyId,
                secretAccessKey: selectedCamWithCredentials.secretAccessKey,
                sessionToken: selectedCamWithCredentials.sessionToken,
                streamName: selectedCamWithCredentials.kvsVideoStreamName,
                region: selectedCamWithCredentials.awsRegionName,
                autoplay: true,
                refreshOnSessionChange: true,
              }"
              :meta="selectedCamWithCredentials"
              :title="selectedCamWithCredentials.name"
              :signal.sync="selectedCamWithCredentials.signal"
              main
              :disable-zoom="true"
              zoomout-on-restore
              show-skeleton
              controller-size="small"
              :show-controllers="false"
              :hide-display-controller="true"
              @onAction="handleVideoStreamEvent"
            >
            </video-stream>
          </section>
          <h5 v-if="selectedCamWithCredentials && showVideo">
            {{ selectedCam.name }}
          </h5>
        </section>
      </main>
      <footer>
        <v-divider class="helpCamSelect_divider" />
        <section class="helpCamSelect-actions">
          <v-btn text class="helpCamSelect-return" @click="handleBack"
            >前の画面に戻る</v-btn
          >
        </section>
      </footer>
    </section>
  </div>
</template>

<script>
import CustomCheck from '@/components/inputs/CustomCheck.vue';
import HelpHeader from './HelpHeader.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import ResizeMixin from '@/mixin/resizeMixin';
import { KVSErrorCodes, PlayerStatus } from '@/globals/enums';
import { videoService } from '@/services/video.service.js';

export default {
  name: 'HelpCamSelect',
  components: {
    HelpHeader,
    CustomCheck,
  },
  mixins: [ResizeMixin],
  data() {
    return {
      selectedCams: {},
      showVideo: true,
      selectedCamId: null,
      attachingCredentials: false,
      credentials: null,
    };
  },
  computed: {
    ...mapGetters('common', {
      cameraList: 'allCamList',
    }),
    ...mapState({
      selectedCam: (state) => state.help.selectedCam,
      iamKey: (state) => state.common.iam.accessKeyId,
      iamSecret: (state) => state.common.iam.secretAccessKey,
    }),
    querySelectedCamId() {
      return this.$route.query.cam;
    },
    items() {
      return this.cameraList.map((item, index) => {
        return {
          value: this.selectedCams[index] && this.selectedCams[index].value,
          label: item.name,
          id: item.id,
        };
      });
    },
    selectedCamWithCredentials() {
      if (!this.selectedCam || !this.credentials) {
        return null;
      }
      return {
        ...this.selectedCam,
        ...this.credentials,
      };
    },
  },
  watch: {
    cameraList: {
      handler(newVal) {
        if (newVal && newVal.length && this.selectedCam) {
          newVal.forEach((item, index) => {
            if (item.id === this.selectedCam.id) {
              this.selectedCams[index] = {
                value: true,
              };
            }
          });
        }
      },
      deep: true,
    },
    $route: {
      handler: function (to, from) {
        this.handleRouteChange(to, from);
      },
      immediate: true,
      deep: true,
    },
    querySelectedCamId: {
      handler(newCamId) {
        if (newCamId && this.cameraList.length) {
          this.initializeCameraFromQuery(newCamId);
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('help', ['setSelectedCam']),
    ...mapActions('common', ['attachCamerasCredentials']),
    async initializeCameraFromQuery(cameraId) {
      const cameraIndex = this.cameraList.findIndex(
        (camera) => camera.id === parseInt(cameraId)
      );

      if (cameraIndex !== -1) {
        const camera = this.cameraList[cameraIndex];
        this.selectedCamId = camera.id;

        // clear previous selections and set current one
        this.selectedCams = {};
        this.selectedCams[cameraIndex] = {
          value: true,
        };

        this.setSelectedCam(camera);
        this.showVideo = false;

        // update credentials before showing video
        await this.updateCredentials(camera.id);

        // show video after credentials are ready
        this.showVideo = true;
      }
    },
    async updateCredentials(cameraId) {
      try {
        this.attachingCredentials = true;

        const credentials = await videoService.attachCameraIamAccessKey([
          { id: cameraId },
        ]);

        this.credentials = {
          accessKeyId: credentials[cameraId].accessKeyId,
          secretAccessKey: credentials[cameraId].secretAccessKey,
          sessionToken: credentials[cameraId].sessionToken,
        };
      } catch (error) {
        console.error(
          'Error attaching credentials for camera:',
          cameraId,
          error
        );
        this.credentials = null;
      } finally {
        this.attachingCredentials = false;
      }
    },
    handleBack() {
      const to = {
        name: 'help-cat-list',
        params: this.$route.params,
        query: this.$route.query,
      };
      this.routerPush(to);
    },
    handleRouteChange(to) {
      if (to.name === 'help-cam-select') {
        if (this.cameraList && this.cameraList.length && this.selectedCam) {
          this.cameraList.forEach((item, index) => {
            if (item.id === this.selectedCam.id) {
              this.selectedCams[index] = {
                value: true,
              };
            }
          });
        }
      }
    },
    handleVideoStreamEvent(e) {
      if (e.type === 'error') {
        const { error, code } = e.payload;
        if (error) {
          switch (code) {
            case KVSErrorCodes.GET_DATA_ENDPOINT_TOKEN_EXP:
              this.handleKVSExpiration({ error, code });
              break;
            case PlayerStatus.StreamingSessionExpired:
              this.handleStreamingSessionExpiration({ error, code });
              break;
            default:
              break;
          }
        }
      }
    },
    async handleKVSExpiration() {
      if (this.attachingCredentials || !this.selectedCamId) {
        return;
      }
      console.warn(
        '[WARN] KVS security token is expired. Renewing token for camera:',
        this.selectedCamId
      );
      await this.updateCredentials(this.selectedCamId);
    },
    async handleStreamingSessionExpiration() {
      if (this.attachingCredentials || !this.selectedCamId) {
        return;
      }
      console.warn(
        '[WARN] Streaming session is expired. Renewing token for camera:',
        this.selectedCamId
      );
      await this.updateCredentials(this.selectedCamId);
    },
    async handleCheck(isChecked, index) {
      if (!isChecked) {
        this.credentials = null;
        this.selectedCamId = null;
        this.routerReplace({ query: {} });
        this.selectedCams = {};
        this.setSelectedCam(null);
        this.showVideo = false;
        return;
      }

      const camera = this.cameraList[index];
      this.selectedCamId = camera.id;
      this.routerReplace({ query: { cam: this.selectedCamId } });

      // clear previous selections
      this.selectedCams = {};
      this.selectedCams[index] = {
        value: isChecked,
      };

      this.setSelectedCam(camera);
      this.showVideo = false;

      // update credentials before showing video
      await this.updateCredentials(camera.id);

      // show video after credentials are ready
      await this.delayPromise(300);
      this.showVideo = true;
    },
    navigateTo(to) {
      this.$router.push(to);
    },
  },
};
</script>

<style lang="scss">
.helpCamSelect {
  position: relative;
  height: calc(100vh - 166px);
  @media (max-width: 1199px) {
    height: auto;
  }
  &_wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 100%;
    padding-bottom: 50px;
    flex: 1;
  }
  &_edit {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    &.v-btn.v-size--default {
      font-size: 15px;
    }
  }
  &_flex-gap {
    display: flex;
    flex: 1;
  }
  &_divider {
    &.theme--light.v-divider {
      border-color: #ccc;
    }
  }
  &_item {
    &--selected {
      background-color: #eee;
    }
  }
  &_live {
    width: 34px;
    height: 17px;
    padding: 0 4px;
    border-radius: 3px;
    background-color: #79c06e;
    font-size: 12px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.3;
    letter-spacing: 0.13px;
    text-align: left;
    color: #fff;
    position: absolute;
    top: 10px;
    left: 10px;
  }
  header {
    height: 15px;
  }
  & main {
    display: flex;
    flex-direction: row;
    @media (max-width: 1199px) {
      flex-direction: column-reverse;
      .helpCamSelect_left {
        margin-right: 0;
      }
    }
  }
  &-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 15px;
  }
  &-return {
    &.v-btn:not(.v-btn--round).v-size--default {
      padding: 0;
    }
  }
  &_left {
    min-width: 300px;
    margin-right: 50px;
    overflow-y: auto;
    .customCheck {
      padding: 12px 10px;
      &:not(:last-child) {
        border-bottom: 1px solid #eee;
      }
      &_label {
        width: 100%;
      }
    }
  }
  &_right {
    margin-top: 11px;
    padding: 0 10px;
    flex: 1;
    width: 100%;
    @media (max-width: 1199px) {
      padding: 0;
      margin-bottom: 10px;
    }
    .helpCamSelect_cam {
      position: relative;
      min-height: 200px;
      width: 100%;
    }

    h5 {
      margin-top: 5px;
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
  }
}
</style>
